import { APP_INITIALIZER, ApplicationConfig, ErrorHandler, importProvidersFrom, provideZonelessChangeDetection } from '@angular/core';
import { CalendarUtils, CalendarA11y, CalendarDateFormatter, CalendarEventTitleFormatter, DateAdapter } from 'angular-calendar';
import { adapterFactory } from 'angular-calendar/date-adapters/date-fns';
import { CustomErrorHandler } from './_services/custom-error-handler';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideRouter, withNavigationErrorHandler } from '@angular/router';
import { routes } from './app.routes';
import { provideHttpClient } from '@angular/common/http';
import { provideAnalytics, getAnalytics } from '@angular/fire/analytics';
import { provideFirebaseApp, initializeApp, getApp } from '@angular/fire/app';
import { provideAuth, getAuth } from '@angular/fire/auth';
import { FIREBASE_OPTIONS } from '@angular/fire/compat';
import { BUCKET } from '@angular/fire/compat/storage';
import { provideDatabase, getDatabase } from '@angular/fire/database';
import { provideFirestore, getFirestore } from '@angular/fire/firestore';
import { providePerformance, getPerformance } from '@angular/fire/performance';
import { provideStorage, getStorage } from '@angular/fire/storage';
import { MAT_DATE_LOCALE } from '@angular/material/core';
import { Directionality } from '@angular/cdk/bidi';
import { dbConfig } from './_configs/db.config';
import { environment } from '../environments/environment';
import { NgxIndexedDBModule } from 'ngx-indexed-db';
import { connectFunctionsEmulator, getFunctions, provideFunctions } from "@angular/fire/functions";
import { DatePipe } from '@angular/common';
import { initializeAppCheck, provideAppCheck, ReCaptchaEnterpriseProvider } from '@angular/fire/app-check';
import { HabitTimerService } from './_services/habit-timer.service';
import { AppDirectionality } from './_services/app-directionality';

export const appConfig: ApplicationConfig = {
  providers: [
    { provide: ErrorHandler, useClass: CustomErrorHandler },
    {
      provide: APP_INITIALIZER,
      useFactory: (timerService: HabitTimerService) => () => {
        timerService.init();
      },
      deps: [HabitTimerService],
      multi: true
    },
    provideZonelessChangeDetection(),
    provideRouter(
      routes,
      // Detect chunk-load failures that surface as router navigation errors
      // (e.g. lazy-loaded routes with stale chunk hashes after a deployment).
      // Uses the same sessionStorage guard as the global unhandledrejection handler
      // in main.ts to prevent infinite reload loops.
      withNavigationErrorHandler((error: unknown) => {
        const isChunkError = (error instanceof TypeError) && (
          (error.message || '').includes('Failed to fetch dynamically imported module') ||
          (error.message || '').includes('Loading chunk') ||
          (error.message || '').includes('Importing a module script failed')
        );
        if (!isChunkError) return;

        const RELOAD_KEY = 'chunk_reload_attempted';
        if (sessionStorage.getItem(RELOAD_KEY)) {
          console.error('[ChunkLoad] Router: chunk load failed after reload, not retrying.', error);
          return;
        }
        console.warn('[ChunkLoad] Router: stale chunk detected, hard-reloading to fetch fresh build.');
        sessionStorage.setItem(RELOAD_KEY, '1');
        window.location.href = window.location.pathname + '?_cb=' + Date.now();
      }),
    ),
    provideAnimations(),
    provideFirebaseApp(() => initializeApp(environment.firebase)),
    ...((environment.env === 'prod' || environment.env === 'hotfix')
      ? [
        provideAppCheck(() => {
          return initializeAppCheck(getApp(), {
            provider: new ReCaptchaEnterpriseProvider(environment.recaptchaSiteKey),
            isTokenAutoRefreshEnabled: true
          });
        }),
      ]
      : []),
    provideFirestore(() => getFirestore()),
    provideDatabase(() => getDatabase()),
    provideAuth(() => getAuth()),
    provideStorage(() => getStorage()),
    providePerformance(() => getPerformance()),
    provideAnalytics(() => getAnalytics()),
    provideFunctions(() => {
      const functions = getFunctions();
      functions.region = environment.functionsRegion;
      if (environment.useLocalFunctions) {
        connectFunctionsEmulator(functions, environment.localFunctionsHost, environment.localFunctionsPort);
        console.log(`[Local Mode] Connected to functions emulator at ${environment.localFunctionsHost}:${environment.localFunctionsPort}`);
      }
      return functions;
    }),
    importProvidersFrom(NgxIndexedDBModule.forRoot(dbConfig)),
    provideHttpClient(),
    {
      provide: BUCKET,
      useValue: environment.firebase.storageBucket,
    },
    { provide: MAT_DATE_LOCALE, useValue: 'en-GB' },
    { provide: Directionality, useClass: AppDirectionality },
    { provide: FIREBASE_OPTIONS, useValue: environment.firebase },
    DatePipe,
    { provide: DateAdapter, useFactory: adapterFactory },
    CalendarUtils,
    CalendarA11y,
    CalendarDateFormatter,
    CalendarEventTitleFormatter,
  ],
};

